import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Popup, useMap, CircleMarker, ZoomControl } from 'react-leaflet';
import { getVehiclePositions } from '../services/api.js';
import StopMarker from './StopMarker';
import VehicleMarker from './VehicleMarker';
import ApproximatedVehicleMarker from './ApproximatedVehicleMarker';
import 'leaflet/dist/leaflet.css';
import 'leaflet-rotatedmarker';
import L from 'leaflet';
import { calculateMapView } from '../utils/map.js';
import { calculateDistance } from '../utils/geo.js';
import MapStateTracker from './map/MapStateTracker';
import useVehicles from '../hooks/useVehicles.js';
import useGdyniaVehicles from '../hooks/useGdyniaVehicles.js';
import useVehicleRoute from '../hooks/useVehicleRoute.js';
import VehicleDataStatus from './map/VehicleDataStatus';
import VehicleRoutePolyline from './map/VehicleRoutePolyline';
import StopBottomSheet from './StopBottomSheet';
import VehicleBottomSheet from './VehicleBottomSheet';

const cartoTileUrl = process.env.NODE_ENV === 'production'
  ? '/odjazdy/api/carto/rastertiles/voyager/{z}/{x}/{y}.png'
  : `https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png${
      process.env.REACT_APP_CARTO_BASEMAPS_API_KEY
        ? `?key=${encodeURIComponent(process.env.REACT_APP_CARTO_BASEMAPS_API_KEY)}`
        : ''
    }`;

// Naprawia problem z ikonami markerów
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ********************************************
// KOMPONENT DO USTAWIANIA WIDOKU
const UserLocationSetter = ({ setUserLocation }) => {
  const map = useMap();
  const [viewSet, setViewSet] = useState(false);

  useEffect(() => {
    if (viewSet) return;
    const DEFAULT_LOCATION = [54.518, 18.540];
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latlng = [position.coords.latitude, position.coords.longitude];
          setUserLocation(latlng);
          map.setView(latlng, 15);
          setViewSet(true);
        },
        (error) => {
          console.error("Błąd geolokalizacji: ", error.message);
          map.setView(DEFAULT_LOCATION, 14);
          setViewSet(true);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    } else {
      console.warn("Geolokalizacja nie jest wspierana.");
      map.setView(DEFAULT_LOCATION, 14);
      setViewSet(true);
    }
  }, [map, setUserLocation, viewSet]);

  return null;
};

// KOMPONENT DO PŁYNNEGO PRZENOSZENIA MAPY
const MapFlyTo = ({ target, offsetPx = 0 }) => {
  const map = useMap();
  useEffect(() => {
    if (target) {
      const zoom = map.getZoom();
      if (offsetPx) {
        const targetPx = map.project(target, zoom);
        const offsetLatLng = map.unproject(
          L.point(targetPx.x, targetPx.y + offsetPx),
          zoom
        );
        map.flyTo(offsetLatLng, zoom, { duration: 0.8 });
      } else {
        map.flyTo(target, zoom, { duration: 0.8 });
      }
    }
  }, [target, map, offsetPx]);
  return null;
};

// PRZYCISK LOKALIZACJI
const LocateButton = ({ userLocation, setUserLocation }) => {
  const map = useMap();

  const handleLocate = () => {
    if (userLocation) {
      map.flyTo(userLocation, 15, { duration: 0.8 });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const latlng = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(latlng);
          map.flyTo(latlng, 15, { duration: 0.8 });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    }
  };

  return (
    <div style={{ position: 'absolute', top: '100px', right: '10px', zIndex: 1000 }}>
      <div className="leaflet-control">
        <button
          className="locate-button"
          onClick={handleLocate}
          aria-label="Moja lokalizacja"
          title="Moja lokalizacja"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <line x1="12" y1="2" x2="12" y2="6" />
            <line x1="12" y1="18" x2="12" y2="22" />
            <line x1="2" y1="12" x2="6" y2="12" />
            <line x1="18" y1="12" x2="22" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
// ********************************************


const MapView = () => {
  console.log(`MapView rendering at – "${new Date().toLocaleTimeString()}"`);

  const [selectedStop, setSelectedStop] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const selectedVehicleRef = useRef(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [routeStops, setRouteStops] = useState([]);

  const [mapCenter, setMapCenter] = useState([54.5188, 18.5305]);
  const [mapZoom, setMapZoom] = useState(13);
  const [userLocation, setUserLocation] = useState(null);
  const [showGdansk, setShowGdansk] = useState(
    () => JSON.parse(localStorage.getItem('filter_showGdansk') ?? 'true')
  );
  const [showGdynia, setShowGdynia] = useState(
    () => JSON.parse(localStorage.getItem('filter_showGdynia') ?? 'true')
  );

  const { vehicles, lastUpdated } = useVehicles(10000);

  // Hook do pobierania polilinii trasy pojazdu
  const todayStr = new Date().toISOString().split('T')[0];
  const routeCoords = useVehicleRoute(
    selectedVehicle?.tripId,
    selectedVehicle?.routeId,
    todayStr
  );

  // Hook aproksymacji pozycji pojazdów gdyńskich
  const { gdyniaVehicles } = useGdyniaVehicles(
    userLocation,
    vehicles,
    30000
  );

  const handleMarkerClick = useCallback((stopData) => {
    setSelectedStop(stopData);
    setSelectedVehicle(null);
    setRouteStops([]);
  }, []);

  const handleVehicleClick = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    selectedVehicleRef.current = vehicle;
    setSelectedStop(null);
    setFlyTarget([vehicle.lat, vehicle.lon]);
  }, []);

  const handleVehicleClose = useCallback(() => {
    setSelectedVehicle(null);
    selectedVehicleRef.current = null;
    setRouteStops([]);
    setFlyTarget(null);
  }, []);

  // Callback from VehicleBottomSheet: timeline loaded with stop coords
  const handleTimelineLoaded = useCallback((timeline) => {
    // Guard: only update if a vehicle is still selected (prevents race condition on close)
    if (!selectedVehicleRef.current) return;
    const stops = timeline
      .filter(s => s.stopLat && s.stopLon)
      .map(s => ({
        stopId: s.stopId,
        stopLat: s.stopLat,
        stopLon: s.stopLon,
        stopDesc: s.stopName,
      }));
    setRouteStops(stops);
  }, []);

  // Callback from VehicleBottomSheet: click on a stop in the list
  const handleStopInRouteClick = useCallback((lat, lon) => {
    setFlyTarget([lat, lon]);
  }, []);

  // Ładowanie wszystkich przystanków do obliczeń (nearby)
  const [allStops, setAllStops] = useState([]);
  const [nearbyStops, setNearbyStops] = useState([]);
  const [favoriteStopsData, setFavoriteStopsData] = useState([]);

  useEffect(() => {
    import('../services/api.js').then(({ fetchStops }) => {
      fetchStops().then(data => {
        setAllStops(data);
      }).catch(err => console.error("Error fetching stops:", err));
    });
  }, []);

  useEffect(() => {
    if (allStops.length === 0) return;
    const centerPoint = userLocation || [54.518, 18.540];
    const [refLat, refLon] = centerPoint;
    const NEARBY_RADIUS_KM = userLocation ? 3 : 5;
    const nearby = allStops.filter(stop => {
      if (!stop.stopLat || !stop.stopLon) return false;
      const dist = calculateDistance(refLat, refLon, stop.stopLat, stop.stopLon);
      return dist <= NEARBY_RADIUS_KM;
    });
    setNearbyStops(nearby);
  }, [userLocation, allStops]);

  const stopsToDisplay = useMemo(() => {
    const favoritesIds = new Set(favoriteStopsData.map(s => s.stopId));
    const nearbyNotFavorite = nearbyStops.filter(s => !favoritesIds.has(s.stopId));
    return [...favoriteStopsData, ...nearbyNotFavorite];
  }, [favoriteStopsData, nearbyStops]);

  // When vehicle selected: show only route stops. Otherwise: default stops (zoom >= 14 only).
  const MIN_ZOOM_FOR_STOPS = 14;
  const showDefaultStops = !selectedVehicle && mapZoom >= MIN_ZOOM_FOR_STOPS;
  const stopsOnMap = selectedVehicle && routeStops.length > 0
    ? routeStops
    : showDefaultStops ? stopsToDisplay : [];



  // Ładowanie ulubionych z LocalStorage
  useEffect(() => {
    const loadFavoriteStopsData = () => {
      const stored = JSON.parse(localStorage.getItem('favoriteStopsData')) || [];
      setFavoriteStopsData(stored);
    };

    loadFavoriteStopsData();
    window.addEventListener('storage', loadFavoriteStopsData);

    return () => {
      window.removeEventListener('storage', loadFavoriteStopsData);
    };
  }, []);

  // Ustawienie widoku na podstawie ulubionych (jeśli brak GPS)
  useEffect(() => {
    if (favoriteStopsData.length > 0 && !userLocation) {
      const { center, zoom } = calculateMapView(favoriteStopsData);
      setMapCenter(center);
      setMapZoom(zoom);
    }
  }, [favoriteStopsData, userLocation]);

  // --- USUNIĘTO STARY USEEFFECT DO POBIERANIA ODJAZDÓW ---
  // Teraz zajmuje się tym StopBottomSheet

  return (
    <div className="map-container">
      {/* Elementy nad mapą (status, filtry) */}
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 500 }}>
        {/* Opcjonalnie można tu przenieść tytuł lub zostawić puste */}
      </div>

      <VehicleDataStatus lastUpdated={lastUpdated} refreshInterval={10000} />

      <div style={{ position: 'absolute', top: '150px', right: '10px', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <button
          className={`vehicle-filter-btn ${showGdansk ? 'active gdansk' : ''}`}
          onClick={() => setShowGdansk(v => { localStorage.setItem('filter_showGdansk', !v); return !v; })}
          title="Pojazdy gdańskie"
        >GD</button>
        <button
          className={`vehicle-filter-btn ${showGdynia ? 'active gdynia' : ''}`}
          onClick={() => setShowGdynia(v => { localStorage.setItem('filter_showGdynia', !v); return !v; })}
          title="Pojazdy gdyńskie"
        >GDY</button>
      </div>

      {/* Usunięto map-controls z checkboxem */}

      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        scrollWheelZoom={false}
        zoomControl={false}
        className="map-view"
        tap={false}
        style={{ height: '100%', width: '100%' }}
      >
        <UserLocationSetter setUserLocation={setUserLocation} />
        <ZoomControl position="topright" />
        <LocateButton userLocation={userLocation} setUserLocation={setUserLocation} />

        <MapStateTracker
          mapCenter={mapCenter}
          mapZoom={mapZoom}
          setMapCenter={setMapCenter}
          setMapZoom={setMapZoom}
        />

        <MapFlyTo target={flyTarget} offsetPx={selectedVehicle ? window.innerHeight * 0.155 : 0} />

        <TileLayer
          url={cartoTileUrl}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          maxZoom={20}
        />

        {/* Marker użytkownika */}
        {userLocation && (
          <CircleMarker
            center={userLocation}
            radius={8}
            pathOptions={{
              color: 'blue',
              fillColor: '#3388ff',
              fillOpacity: 0.8,
              weight: 2
            }}
          >
            <Popup>Jesteś tutaj!</Popup>
          </CircleMarker>
        )}

        {/* Route polyline for selected vehicle */}
        {selectedVehicle && <VehicleRoutePolyline coords={routeCoords} />}

        {/* Markery przystanków — route-only or default */}
        {stopsOnMap.map(stop => (
          <StopMarker
            key={stop.stopId}
            stop={stop}
            onClick={handleMarkerClick}
          />
        ))}

        {/* Markery pojazdów - zawsze włączone, filtrowanie tylko przez API/dane */}
        {showGdansk && vehicles.map(vehicle => (
          vehicle.lat && vehicle.lon ? (
            <VehicleMarker
              vehicle={vehicle}
              key={`vehicle-${vehicle.vehicleId}`}
              onClick={() => handleVehicleClick(vehicle)}
            />
          ) : null
        ))}

        {/* Markery pojazdów gdyńskich - pozycja przybliżona */}
        {showGdynia && gdyniaVehicles.map(vehicle => (
          <ApproximatedVehicleMarker
            vehicle={vehicle}
            key={`approx-${vehicle.vehicleId}`}
            onClick={() => handleVehicleClick(vehicle)}
          />
        ))}
      </MapContainer>

      {/* Bottom Sheet */}
      <StopBottomSheet
        stopId={selectedStop?.stopId}
        stopData={selectedStop}
        onClose={() => setSelectedStop(null)}
      />

      <VehicleBottomSheet
        vehicle={selectedVehicle}
        onClose={handleVehicleClose}
        onStopClick={handleStopInRouteClick}
        onTimelineLoaded={handleTimelineLoaded}
      />
    </div>
  );
};

export default MapView;
